/**
 * Executive Drilldown Card Component
 * Reusable card for Executive Summaries with drill-down navigation
 * 
 * Usage:
 * <ExecutiveDrilldownCard
 *   icon={BarChart3}
 *   title="Formação da Margem EBITDA"
 *   subtitle="Estrutura de absorção de custos"
 *   navigateTo="opex-detail"
 *   setActiveSection={setActiveSection}
 *   color="blue"
 *   language="pt"
 *   tooltip="Explicação da relação entre EBITDA e custos operacionais"
 * >
 *   <p>Content here...</p>
 * </ExecutiveDrilldownCard>
 */

import React, { useState } from 'react';
import { ChevronRight, HelpCircle } from 'lucide-react';

const colorConfig = {
  blue: {
    bg: 'bg-gradient-to-r from-blue-50 to-slate-50',
    border: 'border-blue-100',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    hoverBorder: 'hover:border-blue-300',
    arrow: 'text-blue-400 group-hover:text-blue-600',
  },
  emerald: {
    bg: 'bg-gradient-to-r from-emerald-50 to-slate-50',
    border: 'border-emerald-100',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    hoverBorder: 'hover:border-emerald-300',
    arrow: 'text-emerald-400 group-hover:text-emerald-600',
  },
  amber: {
    bg: 'bg-gradient-to-r from-amber-50 to-slate-50',
    border: 'border-amber-100',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    hoverBorder: 'hover:border-amber-300',
    arrow: 'text-amber-400 group-hover:text-amber-600',
  },
  rose: {
    bg: 'bg-gradient-to-r from-rose-50 to-slate-50',
    border: 'border-rose-100',
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
    hoverBorder: 'hover:border-rose-300',
    arrow: 'text-rose-400 group-hover:text-rose-600',
  },
  purple: {
    bg: 'bg-gradient-to-r from-purple-50 to-slate-50',
    border: 'border-purple-100',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600',
    hoverBorder: 'hover:border-purple-300',
    arrow: 'text-purple-400 group-hover:text-purple-600',
  },
  slate: {
    bg: 'bg-gradient-to-r from-slate-50 to-slate-100',
    border: 'border-slate-200',
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-600',
    hoverBorder: 'hover:border-slate-300',
    arrow: 'text-slate-400 group-hover:text-slate-600',
  },
  gold: {
    bg: 'bg-gradient-to-r from-amber-50/80 to-yellow-50/50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-700',
    hoverBorder: 'hover:border-amber-400',
    arrow: 'text-amber-500 group-hover:text-amber-700',
  },
};

const ExecutiveDrilldownCard = ({
  icon: Icon,
  title,
  subtitle,
  navigateTo,
  setActiveSection,
  color = 'blue',
  language = 'pt',
  children,
  className = '',
  dataTestId,
  tooltip,
}) => {
  const config = colorConfig[color] || colorConfig.blue;
  const isClickable = navigateTo && setActiveSection;
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClick = () => {
    if (isClickable) {
      setActiveSection(navigateTo);
      // Scroll to top after navigation
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div
      className={`${config.bg} rounded-xl p-5 border ${config.border} ${
        isClickable ? `cursor-pointer ${config.hoverBorder} hover:shadow-md transition-all duration-200 group` : ''
      } ${className} relative`}
      onClick={handleClick}
      data-testid={dataTestId || `drilldown-card-${navigateTo || 'static'}`}
    >
      {/* Header with icon, title, tooltip and optional arrow */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${config.iconBg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${config.iconText}`} />
          </div>
          <div className="flex items-center gap-2">
            <div>
              <h4 className="font-semibold text-primary text-sm">{title}</h4>
              {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            </div>
            {/* Tooltip Trigger */}
            {tooltip && (
              <div className="relative">
                <button
                  type="button"
                  className="p-1 rounded-full hover:bg-white/50 transition-colors"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTooltip(!showTooltip);
                  }}
                  data-testid={`tooltip-trigger-${navigateTo || 'static'}`}
                >
                  <HelpCircle className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                </button>
                {/* Tooltip Content */}
                {showTooltip && (
                  <div 
                    className="absolute left-0 top-full mt-2 z-50 w-72 p-4 bg-white rounded-xl shadow-xl border border-slate-200 text-xs"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <div className={`w-6 h-6 rounded-lg ${config.iconBg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-3 h-3 ${config.iconText}`} />
                      </div>
                      <div>
                        <p className="font-semibold text-primary text-xs">{language === 'pt' ? 'Relação com esta análise' : 'Relationship with this analysis'}</p>
                      </div>
                    </div>
                    <p className="text-slate-600 leading-relaxed">{tooltip}</p>
                    <div className="mt-3 pt-2 border-t border-slate-100 flex items-center gap-1 text-slate-400">
                      <ChevronRight className="w-3 h-3" />
                      <span className="text-[10px]">{language === 'pt' ? 'Clique no card para ver mais' : 'Click card to see more'}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {isClickable && (
          <div className={`flex items-center gap-1 ${config.arrow} transition-all duration-200`}>
            <span className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              {language === 'pt' ? 'Ver detalhe' : 'View detail'}
            </span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="text-slate-600 text-xs leading-relaxed">
        {children}
      </div>
    </div>
  );
};

export default ExecutiveDrilldownCard;
